/**
 * 公開用ランディングページ
 * - 未ログインユーザーがアクセスしたときに表示
 * - サービス紹介・機能・プラン・CTA・FAQ 抜粋
 */
import { useEffect, useState } from "react";

const LOGO_URL = "https://21050458.fs1.hubspotusercontent-na2.net/hubfs/21050458/Extension%20Spot/Sign%20Extension%20for%20%E3%82%AF%E3%83%A9%E3%82%A6%E3%83%89%E3%82%B5%E3%82%A4%E3%83%B3.png";
const PRIMARY = "#534AB7";
// 機能セクションで使う線アイコンの統一カラー（青系・細線・丸キャップ）
const ICON_BLUE = "#2563EB";

// Lucide ベースの線アイコン。stroke を ICON_BLUE で統一
const SvgIcon = ({ children, size = 30 }: { children: React.ReactNode; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={ICON_BLUE}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);


const FEATURES: Array<{ icon: React.ReactNode; title: string; desc: string }> = [
  {
    icon: (
      <SvgIcon>
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
      </SvgIcon>
    ),
    title: "ワンクリック送信",
    desc: "HubSpot レコード画面の専用カードから、契約書を即送信。宛先や共有先も HubSpot プロパティから自動取得。",
  },
  {
    icon: (
      <SvgIcon>
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </SvgIcon>
    ),
    title: "ステータス自動同期",
    desc: "送信済み・顧客署名済・締結完了・却下を自動反映。ワークフロー連携で業務自動化。",
  },
  {
    icon: (
      <SvgIcon>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <polyline points="9 15 12 18 15 15" />
      </SvgIcon>
    ),
    title: "締結済み PDF 自動保存",
    desc: "締結完了後、署名済み PDF と合意締結証明書を HubSpot Files に自動アップロード。",
  },
  {
    icon: (
      <SvgIcon>
        <line x1="10" y1="6" x2="21" y2="6" />
        <line x1="10" y1="12" x2="21" y2="12" />
        <line x1="10" y1="18" x2="21" y2="18" />
        <path d="M4 6h1v4" />
        <path d="M4 10h2" />
        <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
      </SvgIcon>
    ),
    title: "固定宛先 + 順序制御",
    desc: "社内承認者を固定宛先に登録して、外部送付前に自動で社内レビュー。宛先順序も柔軟に変更可能。",
  },
  {
    icon: (
      <SvgIcon>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </SvgIcon>
    ),
    title: "複数送信者対応",
    desc: "営業部・法務部などでクラウドサインの送信アカウントを切り替え可能。部署別運用にフィット。",
  },
  {
    icon: (
      <SvgIcon>
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </SvgIcon>
    ),
    title: "アクティビティ自動記録",
    desc: "下書き作成・締結完了のイベントを HubSpot タイムラインに自動記録。履歴が一目で把握できる。",
  },
];

export default function LandingPage() {
  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", color: "#1a1a1a", background: "white" }}>
      <PublicHeader />

      {/* Hero */}
      <section style={{ background: "linear-gradient(135deg, #534AB7 0%, #7F77DD 100%)", color: "white", padding: "80px 24px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", gap: 48, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 360px", minWidth: 320 }}>
            <div style={{ background: "white", display: "inline-block", padding: "10px 16px", borderRadius: 12, marginBottom: 28 }}>
              <img src={LOGO_URL} alt="Sign Extension for クラウドサイン" style={{ height: 40, width: "auto", display: "block" }} />
            </div>
            <h1 style={{ fontSize: 42, fontWeight: 800, lineHeight: 1.25, marginBottom: 20 }}>
              HubSpot CRM から<br />電子契約を直接送信
            </h1>
            <p style={{ fontSize: 18, opacity: 0.95, lineHeight: 1.8, marginBottom: 32 }}>
              HubSpotのレコードからクラウドサインへ送信。
              <br />
              締結ステータスの自動同期、署名済み PDF の自動保存で契約業務を効率化。
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href="/register" style={ctaPrimary}>14日間 無料で始める</a>
              <a href="#features" style={ctaSecondary}>機能を詳しく見る</a>
            </div>
            <div style={{ marginTop: 24, fontSize: 13, opacity: 0.85 }}>
              HubSpot Diamond Partner ナウビレッジ株式会社
            </div>
          </div>
          <div style={{ flex: "1 1 420px", minWidth: 320, maxWidth: 560 }}>
            <HeroValueCards />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ padding: "80px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={sectionTitle}>主な機能</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginTop: 40 }}>
            {FEATURES.map((f) => (
              <div key={f.title} style={featureCard}>
                <div style={{ marginBottom: 14 }}>{f.icon}</div>
                <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{f.title}</div>
                <div style={{ fontSize: 14, color: "#6b6b68", lineHeight: 1.9 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ padding: "80px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={sectionTitle}>料金プラン</h2>
          <p style={{ textAlign: "center", fontSize: 15, color: "#6b6b68", marginTop: 12, marginBottom: 8 }}>
            ご登録から 14 日間、Professional プラン相当を無料でお試しいただけます（カード登録不要）
          </p>
          <p style={{ textAlign: "center", fontSize: 12, color: "#999", marginTop: 0, marginBottom: 40 }}>
            表示価格はすべて税別です。決済時に消費税 10% が加算されます。
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
            {[
              { label: "Starter", price: 4980, sends: "30件", target: "小規模チーム向け", highlighted: false },
              { label: "Professional", price: 9800, sends: "100件", target: "実務利用の中核", highlighted: true },
              { label: "Enterprise", price: 19800, sends: "無制限", target: "大規模運用向け", highlighted: false },
            ].map((p) => (
              <div key={p.label} style={{ ...planCard, ...(p.highlighted ? planCardHighlight : {}), position: "relative" }}>
                {p.highlighted && (
                  <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: PRIMARY, color: "white", padding: "4px 14px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
                    おすすめ
                  </div>
                )}
                <div style={{ fontSize: 18, fontWeight: 700, color: PRIMARY, marginBottom: 8 }}>{p.label}</div>
                <div style={{ fontSize: 32, fontWeight: 800, marginBottom: 4 }}>
                  ¥{p.price.toLocaleString()}
                  <span style={{ fontSize: 14, color: "#6b6b68", fontWeight: 400 }}> / 月（税別）</span>
                </div>
                <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 20 }}>{p.target}</div>
                <div style={{ padding: "14px 0", borderTop: "1px solid #e8e7e3", borderBottom: "1px solid #e8e7e3", fontSize: 14, marginBottom: 20 }}>
                  <div style={{ color: "#6b6b68", fontSize: 12, marginBottom: 4 }}>月間送信数</div>
                  <div style={{ fontWeight: 700 }}>{p.sends}</div>
                </div>
                <a href="/register" style={{ ...ctaPrimary, display: "block", width: "100%", textAlign: "center", padding: "10px", fontSize: 14 }}>
                  このプランで始める
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ snippet */}
      <section style={{ padding: "80px 24px", background: "#faf9f7" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <h2 style={sectionTitle}>よくあるご質問</h2>
          <div style={{ marginTop: 40 }}>
            {[
              { q: "ユーザー権限の設定はできますか？", a: "オーナー / 管理者 / メンバー の 3 ロールでユーザーを管理できます。ユーザー招待・ロール変更・削除はダッシュボードの「ユーザー」タブから操作可能です。" },
              { q: "クラウドサインのアカウントは別途必要ですか？", a: "はい、クラウドサインのスタンダードプラン以上のアカウントが必要です。本サービスはクラウドサインと HubSpot の連携機能を提供するものです。" },
              { q: "締結済みの PDF は HubSpot に保存されますか？", a: "はい。締結完了後、署名済み PDF と合意締結証明書を HubSpot Files に自動アップロードできます。URL のみ保存するモードも選択できます。" },
            ].map((item, i) => (
              <details key={i} style={faqItem}>
                <summary style={faqSummary}>{item.q}</summary>
                <div style={{ padding: "16px 24px 20px", color: "#4a4a48", lineHeight: 1.9, fontSize: 14 }}>
                  {item.a}
                </div>
              </details>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 32 }}>
            <a href="/faq" style={{ color: PRIMARY, textDecoration: "none", fontWeight: 600, fontSize: 15 }}>すべての FAQ を見る →</a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: "80px 24px", background: PRIMARY, color: "white", textAlign: "center" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 16 }}>契約業務の効率化を今すぐ。</h2>
          <p style={{ fontSize: 17, opacity: 0.95, marginBottom: 32, lineHeight: 1.8 }}>
            14 日間の無料トライアルでお試しいただけます。<br />
            HubSpot アカウントとクラウドサインアカウントがあれば 5 分でセットアップ完了。
          </p>
          <a href="/register" style={{ display: "inline-block", padding: "16px 40px", background: "white", color: PRIMARY, borderRadius: 12, fontSize: 17, fontWeight: 700, textDecoration: "none" }}>
            無料で始める
          </a>
        </div>
      </section>

      {/* HubSpot 導入・運用支援 */}
      <section style={{ padding: "60px 24px", background: "#faf9f7" }}>
        <div style={{ maxWidth: 880, margin: "0 auto", display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: "inline-block", padding: "4px 12px", borderRadius: 999, background: "#EEEDFE", color: PRIMARY, fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
              認定パートナーによるサポート
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12, color: "#1a1a1a" }}>HubSpot 導入・運用支援</h2>
            <p style={{ fontSize: 15, color: "#4a4a48", lineHeight: 1.9 }}>
              HubSpot の導入や運用を国内トップクラスの認定パートナーがサポートします。<br />
              お気軽にご相談ください。
            </p>
          </div>
          <div style={{ flexShrink: 0 }}>
            <a
              href="/contact"
              style={{ display: "inline-block", padding: "14px 28px", background: PRIMARY, color: "white", borderRadius: 12, fontSize: 15, fontWeight: 700, textDecoration: "none" }}
            >
              お問い合わせはこちら
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

// ─── Hero 右側の値提示カード ───

/**
 * Hero 右側に表示する 3 つの値提示カード。
 * 半透明白背景でグラデーション上に映えるガラス質感。
 */
function HeroValueCards() {
  const cards = [
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13" />
          <path d="M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      ),
      title: "HubSpot からクラウドサインに連携",
      desc: "HubSpot のレコードからファイルアップロードと宛先指定を行い、そのままクラウドサインに連携。書類作成の手間を削減。",
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1-6.2-8.55" />
          <polyline points="21 4 21 10 15 10" />
        </svg>
      ),
      title: "ステータス自動同期",
      desc: "下書き作成・送信済・締結完了の状態が HubSpot プロパティに自動反映。営業・法務がパイプラインで進捗を一元把握。",
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <polyline points="9 15 11 17 15 13" />
        </svg>
      ),
      title: "署名済み PDF を自動保存",
      desc: "締結完了後、署名済み PDF と合意締結証明書を HubSpot Files へ自動アップロード。契約書の保管漏れを防止。",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {cards.map((c) => (
        <div
          key={c.title}
          style={{
            background: "rgba(255, 255, 255, 0.12)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(255, 255, 255, 0.25)",
            borderRadius: 14,
            padding: "20px 22px",
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              flexShrink: 0,
              width: 48,
              height: 48,
              borderRadius: 12,
              background: "rgba(255, 255, 255, 0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {c.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: "white" }}>
              {c.title}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: "rgba(255, 255, 255, 0.88)" }}>
              {c.desc}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── 共通: 公開ページ用ヘッダー/フッター ───

/**
 * 公開ページ共通ヘッダー
 * @param minimal  true にすると機能/料金/FAQ/お問い合わせ等のナビを消し、ロゴ + 認証状態に応じた CTA のみ表示
 *                 （Contact/Terms/Privacy/FAQ ページで使用）
 */
export function PublicHeader({ minimal = false }: { minimal?: boolean }) {
  // 認証状態は httpOnly Cookie のみで判定する必要があるため、/api/tenants/me を非同期で叩く。
  // 初回描画時は未認証想定でリンクを表示し、認証済みと判明したら差し替え（ちらつきは許容）。
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => {
    fetch("/api/tenants/me", { credentials: "include" })
      .then((r) => setIsAuthenticated(r.ok))
      .catch(() => setIsAuthenticated(false));
  }, []);

  return (
    <header style={{ background: "white", borderBottom: "1px solid #e8e7e3", padding: "16px 24px", position: "sticky", top: 0, zIndex: 10 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <a href="/" style={{ display: "block" }}>
          <img src={LOGO_URL} alt="Sign Extension for クラウドサイン" style={{ height: 36, width: "auto", display: "block" }} />
        </a>
        <nav style={{ display: "flex", gap: 22, alignItems: "center", flexWrap: "wrap" }}>
          {!minimal && (
            <>
              <a href="/#features" style={navLink}>機能</a>
              <a href="/#pricing" style={navLink}>料金</a>
              <a href="/faq" style={navLink}>FAQ</a>
              <a href="/contact" style={navLink}>お問い合わせ</a>
            </>
          )}
          {isAuthenticated ? (
            <a href="/dashboard" style={{ ...ctaPrimary, padding: "9px 20px", fontSize: 14 }}>ダッシュボードへ</a>
          ) : (
            <>
              <a href="/login" style={navLink}>ログイン</a>
              <a href="/register" style={{ ...ctaPrimary, padding: "9px 20px", fontSize: 14 }}>無料ではじめる</a>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

/**
 * 公開ページ共通フッター
 * @param minimal  true にするとカテゴリ見出し（サービス/リーガル/運営）を外し、
 *                 FAQ・利用規約・個人情報・ナウビレッジ・お問い合わせをフラットに並べる
 */
export function PublicFooter({ minimal = false }: { minimal?: boolean }) {
  const flatLinks = [
    { label: "FAQ", href: "/faq", external: false },
    { label: "利用規約", href: "/terms", external: false },
    { label: "個人情報の取扱い", href: "/privacy", external: false },
    { label: "ナウビレッジ株式会社", href: "https://now-village.jp", external: true },
    { label: "お問い合わせ", href: "/contact", external: false },
  ];

  return (
    <footer style={{ background: "#1a1a1a", color: "white", padding: "48px 24px 32px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 40, flexWrap: "wrap", marginBottom: 40 }}>
          <div style={{ flex: "1 1 280px" }}>
            <div style={{ background: "white", display: "inline-block", padding: "8px 12px", borderRadius: 8, marginBottom: 16 }}>
              <img src={LOGO_URL} alt="Sign Extension for クラウドサイン" style={{ height: 32, width: "auto", display: "block" }} />
            </div>
            <p style={{ fontSize: 13, opacity: 0.7, lineHeight: 1.8 }}>
              HubSpot とクラウドサインを連携し、契約業務を自動化する拡張アプリ。
            </p>
          </div>

          {minimal ? (
            // フラットなリンクリスト（Contact/Terms/Privacy/FAQ 用）
            <div style={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: "14px 28px" }}>
              {flatLinks.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target={l.external ? "_blank" : undefined}
                  rel={l.external ? "noopener noreferrer" : undefined}
                  style={{ ...footerLink, marginBottom: 0 }}
                >
                  {l.label}
                </a>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 48, flexWrap: "wrap" }}>
              <div>
                <div style={footerHeading}>サービス</div>
                <a href="/#features" style={footerLink}>機能</a>
                <a href="/#pricing" style={footerLink}>料金プラン</a>
                <a href="/faq" style={footerLink}>FAQ</a>
              </div>
              <div>
                <div style={footerHeading}>リーガル</div>
                <a href="/terms" style={footerLink}>利用規約</a>
                <a href="/privacy" style={footerLink}>個人情報の取扱い</a>
              </div>
              <div>
                <div style={footerHeading}>運営</div>
                <a href="https://now-village.jp" target="_blank" rel="noopener noreferrer" style={footerLink}>ナウビレッジ株式会社</a>
                <a href="/contact" style={footerLink}>お問い合わせ</a>
              </div>
            </div>
          )}
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 24, fontSize: 12, opacity: 0.6 }}>
          © {new Date().getFullYear()} ナウビレッジ株式会社 / Sign Extension for クラウドサイン
        </div>
      </div>
    </footer>
  );
}

// ─── Styles ───
const sectionTitle: React.CSSProperties = { fontSize: 32, fontWeight: 800, textAlign: "center", letterSpacing: -0.5 };
const ctaPrimary: React.CSSProperties = {
  display: "inline-block", padding: "14px 32px", background: "white", color: PRIMARY,
  borderRadius: 12, fontSize: 16, fontWeight: 700, textDecoration: "none",
};
const ctaSecondary: React.CSSProperties = {
  display: "inline-block", padding: "14px 32px", border: "2px solid white", color: "white",
  borderRadius: 12, fontSize: 16, fontWeight: 700, textDecoration: "none",
};
const featureCard: React.CSSProperties = {
  background: "#faf9f7", borderRadius: 12, padding: "28px 24px",
};
const planCard: React.CSSProperties = {
  background: "white", border: "1px solid #e8e7e3", borderRadius: 16, padding: "32px 24px",
};
const planCardHighlight: React.CSSProperties = {
  border: `2px solid ${PRIMARY}`, boxShadow: `0 8px 24px ${PRIMARY}22`,
};
const faqItem: React.CSSProperties = {
  background: "white", borderRadius: 10, marginBottom: 10, overflow: "hidden", border: "1px solid #e8e7e3",
};
const faqSummary: React.CSSProperties = {
  padding: "16px 24px", fontSize: 15, fontWeight: 600, cursor: "pointer", listStyle: "none",
};
const navLink: React.CSSProperties = {
  color: "#1a1a1a", textDecoration: "none", fontSize: 14, fontWeight: 500,
};
const footerHeading: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, marginBottom: 14, opacity: 0.9,
};
const footerLink: React.CSSProperties = {
  display: "block", color: "white", opacity: 0.65, textDecoration: "none", fontSize: 13, marginBottom: 8,
};
